/*
  MIT — Warenkorb-Empfehlungen "Passt gut dazu" + Schnellansicht
  Angelegt 04.09.2026 · Mark VIII (Shopify) · Test-Theme "Dawn - Work 01.09.26"

  Lädt sections/mit-cart-upsell.liquid über den Empfehlungs-Endpunkt nach.
  08.09.2026: Der Button öffnet die Schnellansicht (sections/mit-quick-view.liquid,
  geladen über /products/<handle>?section_id=mit-quick-view). Aus der Schnellansicht
  heraus wird die Variante gewählt und per AJAX in den Warenkorb gelegt.
*/
class MitCartUpsell extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === '1') return;

    // Es gibt zwei Ausspielungen (seitliche Spalte ab 990 px, im Warenkorb darunter).
    // Nur die sichtbare lädt, sonst gäbe es zwei Anfragen für dieselben Empfehlungen.
    if (getComputedStyle(this).display === 'none') {
      if (!this.dataset.warten) {
        this.dataset.warten = '1';
        window.addEventListener('resize', () => this.connectedCallback(), { once: true });
      }
      return;
    }

    this.dataset.loaded = '1';

    const productId = this.dataset.productId;
    if (!productId) {
      this.innerHTML = '';
      return;
    }

    const base = this.dataset.url || '/recommendations/products';
    const params = new URLSearchParams({
      section_id: this.dataset.sectionId || 'mit-cart-upsell',
      product_id: productId,
      limit: this.dataset.limit || '10',
      intent: this.dataset.intent || 'related'
    });

    fetch(`${base}?${params.toString()}`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const markup = new DOMParser().parseFromString(text, 'text/html').querySelector('.cart-upsell');
        this.innerHTML = markup ? markup.outerHTML : '';
        this.bindButtons();
      })
      .catch(() => {
        this.innerHTML = '';
      });
  }

  bindButtons() {
    this.querySelectorAll('.upsell-add').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        MitQuickView.open(button.dataset.quickView, button.dataset.productTitle);
      });
    });
  }
}

/* Schnellansicht als Overlay über dem Warenkorb */
const MitQuickView = {
  element: null,

  aufbauen() {
    if (this.element) return this.element;

    const wrap = document.createElement('div');
    wrap.className = 'mit-qv-modal';
    wrap.hidden = true;
    wrap.innerHTML = [
      '<div class="mit-qv-modal__overlay" data-schliessen></div>',
      '<div class="mit-qv-modal__panel" role="dialog" aria-modal="true" aria-label="Schnellansicht" tabindex="-1">',
      '  <button class="mit-qv-modal__close" type="button" aria-label="Schließen" data-schliessen>&times;</button>',
      '  <div class="mit-qv-modal__content"></div>',
      '</div>'
    ].join('');

    wrap.addEventListener('click', (event) => {
      if (event.target.hasAttribute('data-schliessen')) this.schliessen();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.element && !this.element.hidden) this.schliessen();
    });

    document.body.appendChild(wrap);
    this.element = wrap;
    return wrap;
  },

  open(produktUrl, titel) {
    if (!produktUrl) return;

    const wrap = this.aufbauen();
    const inhalt = wrap.querySelector('.mit-qv-modal__content');
    inhalt.innerHTML = '<p class="mit-qv-modal__laden">' + (titel || 'Produkt') + ' wird geladen …</p>';
    wrap.hidden = false;
    wrap.classList.add('is-open');
    wrap.querySelector('.mit-qv-modal__panel').focus();

    const trenner = produktUrl.indexOf('?') === -1 ? '?' : '&';

    fetch(`${produktUrl}${trenner}section_id=mit-quick-view`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const markup = new DOMParser().parseFromString(text, 'text/html').querySelector('.mit-qv');
        if (!markup) throw new Error('kein Inhalt');
        inhalt.innerHTML = markup.outerHTML;
        this.binden(inhalt);
      })
      .catch(() => {
        inhalt.innerHTML =
          '<p class="mit-qv-modal__laden">Die Schnellansicht lässt sich gerade nicht laden. ' +
          '<a href="' + produktUrl + '">Zur Produktseite</a></p>';
      });
  },

  schliessen() {
    if (!this.element) return;
    this.element.classList.remove('is-open');
    this.element.hidden = true;
    this.element.querySelector('.mit-qv-modal__content').innerHTML = '';
  },

  binden(inhalt) {
    const select = inhalt.querySelector('[data-variant-select]');
    const button = inhalt.querySelector('.mit-qv__add');
    const preis = inhalt.querySelector('[data-preis]');

    if (select && button) {
      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        button.dataset.variantId = option.value;
        if (!preis) return;

        const teile = ['<span class="price-sale">' + option.dataset.preis + '</span>'];
        if (option.dataset.streichpreis) {
          teile.push('<span class="price-compare">' + option.dataset.streichpreis + '</span>');
          const badge = inhalt.querySelector('.price-badge');
          const text = badge && !/^-/.test(badge.textContent.trim())
            ? badge.textContent.trim()
            : '-' + option.dataset.rabatt + '%';
          teile.push('<span class="price-badge">' + text + '</span>');
        }
        preis.innerHTML = option.dataset.streichpreis
          ? teile.join(' ')
          : '<span class="price-normal">' + option.dataset.preis + '</span>';
      });
    }

    if (button) {
      button.addEventListener('click', () => this.inDenWarenkorb(button));
    }
  },

  inDenWarenkorb(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const label = button.textContent;
    button.disabled = true;
    button.textContent = '…';

    const drawer = document.querySelector('cart-drawer');
    const sections = drawer ? drawer.getSectionsToRender().map((section) => section.id) : [];
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(`${root.replace(/\/$/, '')}/cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sections,
        sections_url: window.location.pathname
      })
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) throw new Error(state.description || state.message);

        this.schliessen();

        if (drawer && state.sections) {
          drawer.renderContents(state);
        } else {
          window.location.reload();
        }

        document.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'mit-quick-view' } })
        );
      })
      .catch(() => {
        button.disabled = false;
        button.textContent = label;
      });
  }
};

if (!customElements.get('mit-cart-upsell')) {
  customElements.define('mit-cart-upsell', MitCartUpsell);
}

